import { useEffect, useState } from "react";
import { Phone, Trash2, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { STATUS_LABELS, STATUS_FLOW } from "@/lib/constants";
import { toast } from "sonner";

export default function AdminRepairs() {
  const [repairs, setRepairs] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/repairs").then((r) => setRepairs(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/repairs/${id}`, { status });
      toast.success("Status aktualisiert");
      load();
    } catch (e) { toast.error("Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Reparatur wirklich löschen?")) return;
    await api.delete(`/admin/repairs/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const filtered = repairs.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search && !`${r.customer_name} ${r.phone} ${r.model}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="admin-repairs">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Service</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Reparaturen</h1>
        </div>
        <div className="text-sm text-zinc-500">{filtered.length} von {repairs.length}</div>
      </div>

      <div className="bg-[#141417] rounded-2xl border border-white/5 p-4 mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-64">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Suche Name, Telefon, Modell..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#0A0A0B] border-white/10 text-white" data-testid="repairs-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56 bg-[#0A0A0B] border-white/10 text-white" data-testid="repairs-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141417] border-white/10 text-white">
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#141417] rounded-2xl border border-white/5 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Kunde</th>
              <th className="text-left px-5 py-3">Telefon</th>
              <th className="text-left px-5 py-3">Gerät</th>
              <th className="text-left px-5 py-3">Problem</th>
              <th className="text-left px-5 py-3">Preis</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-white/5">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-white/5" data-testid={`repair-row-${r.id}`}>
                <td className="px-5 py-3.5 font-medium text-white">{r.customer_name}</td>
                <td className="px-5 py-3.5 text-zinc-400">
                  <a href={`tel:${r.phone}`} className="hover:text-[#FF2D40] flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</a>
                </td>
                <td className="px-5 py-3.5 text-zinc-400">{r.brand} {r.model}</td>
                <td className="px-5 py-3.5 text-zinc-400">{r.issue}</td>
                <td className="px-5 py-3.5 font-medium text-white numeric">€{r.estimated_price || 0}</td>
                <td className="px-5 py-3.5">
                  <Select value={r.status} onValueChange={(s) => updateStatus(r.id, s)}>
                    <SelectTrigger className={`w-44 text-xs border ${STATUS_LABELS[r.status]?.color || ""}`} data-testid={`status-select-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141417] border-white/10 text-white">
                      {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="hover:bg-white/5" data-testid={`delete-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center py-12 text-zinc-500">Keine Reparaturen gefunden</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
